// EventCard.tsx with improved notification handling

import { useEffect, useState } from 'react';
import { Card, Image, Text, Badge, Group, Box, Title, ActionIcon, Skeleton } from '@mantine/core';
import { IconCalendar, IconMapPin, IconClock, IconTicket, IconBookmark } from '@tabler/icons-react';
import { showNotification } from '@mantine/notifications';
import { useUser } from '../../src/context/UserContext';

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  event_type_ids: number[];
  event_types: { id: number; name: string }[];
  dance_styles?: { id: number; name: string }[];
  venue_name: string;
  address: string;
  city?: { name: string };
  state?: { name: string };
  country?: {
    name: string;
    flag_emoji: string;
  };
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  event_type?: string;
  dance_style?: string;
  [key: string]: any; // Allows additional fields
}

interface EventCardProps {
  event: Event;
  onCardClick?: (event: Event) => void;
  onBookmarkClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export default function EventCard({ event, onCardClick, onBookmarkClick }: EventCardProps) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const { user } = useUser();

  // Check if event is bookmarked using localStorage
  useEffect(() => {
    if (user) {
      // Get bookmarked events from localStorage
      const bookmarkedEvents = JSON.parse(localStorage.getItem('bookmarkedEvents') || '[]');
      const isEventBookmarked = bookmarkedEvents.includes(event.slug);
      setIsBookmarked(isEventBookmarked);
    }
  }, [event.slug, user]);

  // Format dates for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Format full date for display
  const formatFullDate = (startDate: string, endDate: string) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Base options for the *start* date
    const startOpts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    };

    // If it's exactly the same calendar day:
    if (
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate()
    ) {
      return start.toLocaleDateString('en-US', startOpts);
    }

    // Always format the start the same way:
    const startStr = start.toLocaleDateString('en-US', startOpts);

    // Decide how to format the end:
    // – Same month? only show day
    // – Different month? show month+day
    const endOpts: Intl.DateTimeFormatOptions =
      start.getMonth() === end.getMonth() ? { day: 'numeric' } : { month: 'short', day: 'numeric' };

    const endStr = end.toLocaleDateString('en-US', endOpts);

    return `${startStr}–${endStr}`;
  };

  const startDate = formatDate(event.event_start_date);
  const startTime = formatTime(event.event_start_date);
  const endTime = formatTime(event.event_end_date);

  // Get country flag emoji
  const getCountryFlag = (country?: { name: string; flag_emoji: string }) => {
    // Fallback to globe emoji if object missing
    if (!country) return '🌐';
    return country.flag_emoji || '🇺🇸';
  };

  // Determine if the event is in the past
  const isPastEvent = new Date(event.event_end_date) < new Date();

  // Handle card click
  const handleCardClick = (e: React.MouseEvent) => {
    // If clicking on a link or button inside the card, don't trigger the drawer
    if ((e.target as HTMLElement).closest('a') || (e.target as HTMLElement).closest('button')) {
      return;
    }

    // Prevent default behavior
    e.preventDefault();
    e.stopPropagation();

    // Call the parent's onCardClick if provided
    if (onCardClick) {
      onCardClick(event);
    }
  };

  // Handle bookmark toggle
  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click

    if (!user) {
      showNotification({
        title: 'Authentication Required',
        message: 'Please log in to bookmark events',
        color: 'red',
        autoClose: 5000,
      });
      return;
    }

    // Toggle bookmark state optimistically
    const newBookmarkedState = !isBookmarked;
    setIsBookmarked(newBookmarkedState);

    // Update localStorage
    const bookmarkedEvents = JSON.parse(localStorage.getItem('bookmarkedEvents') || '[]');
    if (newBookmarkedState) {
      // Add to bookmarked events if not already present
      if (!bookmarkedEvents.includes(event.slug)) {
        bookmarkedEvents.push(event.slug);
      }
    } else {
      // Remove from bookmarked events
      const index = bookmarkedEvents.indexOf(event.slug);
      if (index > -1) {
        bookmarkedEvents.splice(index, 1);
      }
    }
    localStorage.setItem('bookmarkedEvents', JSON.stringify(bookmarkedEvents));

    // Make request to the upvote endpoint
    fetch(`/events/${event.slug}/upvote.json`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      credentials: 'same-origin',
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        // Show success notification
        showNotification({
          title: isBookmarked ? 'Bookmark Removed' : 'Event Bookmarked',
          message: isBookmarked
            ? `"${event.name}" has been removed from your bookmarks`
            : `"${event.name}" has been added to your bookmarks`,
          color: 'green',
          autoClose: 5000,
        });
      })
      .catch((error) => {
        console.error('Error:', error);

        // Revert optimistic update
        setIsBookmarked(!newBookmarkedState);

        // Revert localStorage change
        if (!newBookmarkedState) {
          // Add back to bookmarked events
          if (!bookmarkedEvents.includes(event.slug)) {
            bookmarkedEvents.push(event.slug);
          }
        } else {
          // Remove from bookmarked events
          const index = bookmarkedEvents.indexOf(event.slug);
          if (index > -1) {
            bookmarkedEvents.splice(index, 1);
          }
        }
        localStorage.setItem('bookmarkedEvents', JSON.stringify(bookmarkedEvents));

        // Show error notification
        showNotification({
          title: 'Error',
          message: 'Failed to update bookmark status',
          color: 'red',
          autoClose: 5000,
        });
      });
  };

  return (
    <Card
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        backgroundColor: '#fff7ed1a',
        border: '1px solid #f2f2f230',
        cursor: onCardClick ? 'pointer' : 'default', // Change cursor to pointer if clickable
      }}
      onClick={handleCardClick} // Add onClick handler
    >
      {/* Image Section with Event Type Badge */}
      <Card.Section style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden' }}>
        {!imageLoaded && <Skeleton height={200} radius="md" animate={true} />}
        {/* Event Image */}
        <Image
          src={event.banner_url || 'https://placehold.co/600x400?text=No+Image'}
          alt={event.name}
          placeholder={<Skeleton height="100%" radius="md" animate />}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
          onLoad={() => setImageLoaded(true)}
        />

        {/* Event Type Badges - Upper Left Corner */}
        {event.event_types && event.event_types.length > 0 && (
          <Group
            style={{
              position: 'absolute',
              top: 10, // Adjust spacing from the top
              left: 10, // Adjust spacing from the left
              zIndex: 10, // Ensure it appears above the image
              gap: '0.3rem',
            }}
          >
            {event.event_types.map((type) => (
              <Badge key={type.id} variant="filled" color="rgba(0, 0, 0, 0.63)" size="sm" className="poppins">
                {type.name}
              </Badge>
            ))}
          </Group>
        )}

        {/* Bookmark Icon - Lower Right Corner */}
        <ActionIcon
          variant="transparent"
          style={{
            position: 'absolute',
            bottom: 10,
            right: 10,
            width: 35,
            height: 35,
            zIndex: 10,
            background: 'rgba(0, 0, 0, 0.3)',
            borderRadius: '50%',
          }}
          onClick={(e) => {
            if (onBookmarkClick) {
              onBookmarkClick(e);
            } else {
              handleBookmarkToggle(e);
            }
          }}
        >
          <IconBookmark
            size={28}
            style={{
              fill: isBookmarked ? '#EA580C' : 'transparent',
              color: isBookmarked ? '#EA580C' : 'white',
            }}
          />
        </ActionIcon>
      </Card.Section>

      {/* Info Section */}
      <Box
        px="lg"
        style={{
          backgroundColor: 'rgba(28, 28, 28, 0.95)',
          color: 'white',
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Group
          style={{
            gap: '0.1rem',
          }}
        >
          <IconCalendar size={12} className="mt-2" style={{ color: '#b8b8b8' }} />
          <Text
            size="sm"
            c="gray.4"
            className="poppins mt-2"
            style={{ lineHeight: 1, color: '#b8b8b8', fontSize: '10px' }}
          >
            {formatFullDate(event.event_start_date, event.event_end_date)}
          </Text>
          <IconClock className="mt-2 ml-1" size={12} style={{ color: '#b8b8b8' }} />
          <Text
            size="sm"
            c="gray.4"
            className="poppins mt-2"
            style={{ lineHeight: 1, color: '#b8b8b8', fontSize: '10px' }}
          >
            {startTime}–{endTime}
          </Text>
        </Group>

        <Title order={4} lineClamp={2} style={{ color: '#FFF7ED', lineHeight: 1.2 }} className="rubik my-1">
          {event.name}
        </Title>

        <Group mb="8px" mt="4px" gap="2px" style={{ flexWrap: 'nowrap', maxWidth: '100%' }}>
          <IconMapPin size={20} style={{ color: '#b8b8b8', flexShrink: 0 }} />
          <Text
            lineClamp={2}
            style={{
              lineHeight: 1.2,
              color: '#b8b8b8',
              fontSize: '12px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              letterSpacing: '-0.3px',
            }}
            className="poppins"
          >
            {event.city && ` ${event.city.name}`}
            {event.state && `, ${event.state}`}
            {event.country && ` ${getCountryFlag(event.country)}`}
          </Text>
        </Group>

        {/* Dance Styles - New Section */}
        {event.dance_styles && event.dance_styles.length > 0 && (
          <Group gap="4px" mt="4px" mb="md" wrap="wrap">
            {event.dance_styles.slice(0, 3).map((style) => (
              <Badge
                key={style.id}
                variant="filled"
                color="#EA580C"
                radius="xl"
                size="xs"
                className="poppins"
                style={{ backgroundColor: '#EA580C', textTransform: 'none' }}
              >
                {style.name}
              </Badge>
            ))}
            {event.dance_styles.length > 3 && (
              <Text size="xs" className="poppins" style={{ color: '#d1d1d1', fontSize: 12 }}>
                + {event.dance_styles.length - 3}
              </Text>
            )}
          </Group>
        )}

        {/* Keeping the legacy dance_style string condition for backward compatibility */}
        {!event.dance_styles && event.dance_style && (
          <Group mb="sm">
            <Text size="sm" style={{ color: '#FFF7ED' }} className="poppins">
              Style: {event.dance_style}
            </Text>
          </Group>
        )}
      </Box>
    </Card>
  );
}
